import React, { useEffect } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { RootState } from "us.helper/types";
import { AddCategorySchema } from "../../Validations";
import * as Actions from "actions";
import { SaveCategory } from "components/EditorHome/Repository";
import { $Radio } from "us.common/components";
import { ADAPTER_TYPE_OPTION } from "components/EditorHome/Constants";
import { filterAdapters, getCategoryDisplayNamesByGroup, getCategoryNamesByGroup, setSchemaForRPTAdapters } from "../../Functions";
import { Group } from "../../Interfaces";
import { AdapterType } from "../../Constants";

const { $Button, $Row, $Col, $Input, $Select, $Form, $Popconfirm } =
  Common.Components;

/**
 * @description - Add new template category
 * @link Design Document -
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 10/10/2023
 */
interface IAddCategory {
  onClose: any;
  group:Group;
}

const AddCategory: React.FC<PropsFromRedux & IAddCategory> = (props) => {
  const { t } = useTranslation();

  const {
    templates,
    onClose,
    dataAdapters,
    isCategorySaving,
    addCategoryInitial,
    group,
    getDataAdapterList,
    saveNewCategory,
  } = props;
  const { groups } = templates ?? {};

  useEffect(() => {
    getDataAdapterList && getDataAdapterList({});
  }, []);

  /**
   * @description Handle submit button action
   * @param {any} values
   */
  const handleSubmit = (values: any) => {
    const adapter = dataAdapters?.data?.find(
      (adapter: any) => adapter.name == values?.spName
    );
    const sp = `[${adapter?.schema}].[${values?.spName}]`;
    const group = groups?.find(
      (group: any) => group.groupId == values?.categoryGroupId
    );
    const rptAdapters = setSchemaForRPTAdapters(
      dataAdapters?.data,
      values?.rptSPName
    );
    saveNewCategory &&
      saveNewCategory(
        SaveCategory.call({
          ...values,
          sp,
          categoryGroup: group?.groupName,
          rptAdapters,
        })
      );
    onClose && onClose();
  };

  return (
    <Formik
      initialValues={{ ...addCategoryInitial, categoryGroupId: group.id }}
      enableReinitialize
      validationSchema={AddCategorySchema(
        getCategoryNamesByGroup(groups, group?.name),
        getCategoryDisplayNamesByGroup(groups, group?.name)
      )}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, values, ...restProps }: any) => (
        <$Form layout="vertical">
          <div className="mb-2">
            <$Select
              name="categoryGroupId"
              formitem={{
                label: t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.GROUP"),
              }}
              size="small"
              options={groups}
              optionValue="groupId"
              optionText="groupName"
              allOption={false}
              disabled={true}
              defaultValue={group.name}
              required
            />
          </div>
          <div className="mb-2">
            <$Input
              required
              size="small"
              name="categoryName"
              label={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.APPLICATION_NAME")}
              disabled={isCategorySaving}
            />
          </div>
          <div className="mb-3">
            <$Input
              required
              size="small"
              name="displayName"
              label={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DISPLAY_NAME")}
              disabled={isCategorySaving}
            />
          </div>
          <div className="mb-3">
            <div>
                {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADAPTER_TYPE")}
              </div>
            <$Radio
              name="adapterType"
              className="m-0"
              size="small"
              options={ADAPTER_TYPE_OPTION}
              optionValue="value"
              optionText="label"
            />
          </div>
          <div className="mb-2">
            <$Input
              required
              size="small"
              name="adapterName"
              label={t(
                "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.STANDARD_ADAPTER_NAME"
              )}
              disabled={isCategorySaving}
            />
          </div>
          <div className="mb-2">
            <$Select
              name="spName"
              formitem={{
                label: values?.adapterType == AdapterType.SP? t(
                    "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADAPTER_SP_NAME"
                  ): t(
                        "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADAPTER_API_NAME"
                      )
              }}
              size="small"
              options={filterAdapters(
                dataAdapters?.data,
                values.categoryGroupId,
                false,
                groups
              )}
              optionValue="name"
              optionText="name"
              allOption={false}
              required
              onSearchBy={["name"]}
              disabled={isCategorySaving}
            />
          </div>
          <div className="mb-3">
            <$Select
              name="rptSPName"
              formitem={{
                label: t(
                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.REPEAT_ADAPTERS"
                ),
              }}
              size="small"
              options={filterAdapters(
                dataAdapters?.data,
                values.categoryGroupId,
                true,
                groups
              )}
              optionValue="name"
              optionText="name"
              allOption={false}
              mode={"multiple"}
              onSearchBy={["name"]}
              disabled={isCategorySaving}
            />
          </div>

          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Popconfirm
                title={t("COMMON.SURE_TO_PROCEED_?")}
                placement="topLeft"
                onConfirm={()=>handleSubmit()}
                okText={t("COMMON.YES")}
                cancelText={t("COMMON.NO")}
                disabled={isCategorySaving|| !restProps.isValid}
              >
                <$Button
                  className="ml-3 mr-2"
                  type="primary"
                  htmlType="submit"
                  disabled={isCategorySaving || !restProps.isValid}
                >
                  {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                </$Button>
              </$Popconfirm>
              <$Button onClick={()=>onClose()}>
                {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </div>
        </$Form>
      )}
    </Formik>
  );
};
const mapStateToProps = (state: RootState) => {
  const { templatesHistory, editorHome } = state;
  const { templates } = templatesHistory;
  const { dataAdapters, isCategorySaving, addCategoryInitial } = editorHome;
  return {
    templates,
    dataAdapters,
    isCategorySaving,
    addCategoryInitial,
  };
};

const { editorHome } = Actions;
const { dataAdapterList, templateCategory } = editorHome;

const mapDispatchToProps = {
  getDataAdapterList: dataAdapterList.get,
  saveNewCategory: templateCategory.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AddCategory);
